import React, {memo, useRef, useState} from 'react';
import parse from 'html-react-parser';
import axios from "axios";
import {Button, IconButton} from "@material-ui/core";
import {AddBoxOutlined, DeleteOutline} from "@material-ui/icons";
import {FileDownload} from "@mui/icons-material";
import {withStyles} from "@material-ui/core/styles";
import { getApiSitePrefix } from "../../../functions/SiteDefaults";
import DataGrid from "../../../controls/DataGrid";
import {DropzoneFileUpload} from "../../../controls";

const apiSitePrefix = getApiSitePrefix();

const styles = {
  header: {
    "&.ag-header-cell": {
      paddingLeft: 3, paddingRight: 3, fontSize: 12
    }
  },
  column: {
    "&.ag-cell-not-inline-editing": {
      paddingLeft:4, paddingRight: 4
    }
  },
  numericHeader: {
    "&.ag-header-cell": {
      paddingRight: 1, fontSize: 12,
    },
    // "&.ag-cell-label-container": {
    //   flexDirection: "row"
    // },
    // "&.ag-header-cell-label": {
    //   flexDirection: "rowReverse"
    // },
  },
  numericColumn: {
    "&.ag-cell-not-inline-editing": {
      paddingLeft:4, paddingRight: 4, textAlign: "right"
    }
  },
};

const BookingDimensionsGrid = (_props) => {
  const props = {
    ..._props
  }
  const [gridApi, setGridApi] = useState(null);
  const [gridColumnApi, setGridColumnApi] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [state, setState] = useState({
    canEdit: props.canEdit||false,
    activeRow: null,
    showAddDialog: false,
    addButtonSuffix: "",
  });
  if (props.canEdit && !state.canEdit) {
    setState({...state, canEdit: props.canEdit});
    // if (gridApi)
    //   gridApi.redrawRows();
  }

  const SystemData = props.systemData;

  const dataGridRef = useRef();
  const dimensions = props.dimensions;
  const record = props.record;
  const spotBooking = record?.spotBooking;
  const specialLoad = record?.formMode === "SLR";
  const defaults = props.defaults;
  const formMode = props.formMode;
  const internalUser = formMode === "OPERATOR";

  // ==================
  // Grid setup
  // ==================
  const { classes } = props;
  const onGridReady = (params) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
    params.api.sizeColumnsToFit();
    if (props.onGridReady) {
      props.onGridReady(params);
    }
  };

  const onRowSelected = (params) => {
    if (params.type === "rowSelected") {
      setSelectedRow({
        rowIndex: params.rowIndex,
        rowData: params.data
      })
    }
    else {
      setSelectedRow(null);
    }
  };

  function setAutoHeight() {
    if (gridApi.getDisplayedRowCount() >= 3) {
      gridApi.setDomLayout('autoHeight');
      // auto height will get the grid to fill the height of the contents,
      // so the grid div should have no height set, the height is dynamic.
      document.querySelector('#myGrid').style.height = '';
    }
  }

  const onRowDataUpdated = (params) => {
    //createSummaryData(params.api);
    createSummaryData(params);
  }

  const checkRestrictions = async (dimensions) => {
    const {maxLength, maxWidth, maxHeight, cargoDoorHeightLimit} = defaults;

    let heightLimitExceeded = false;
    let spotThresholdExceeded = false;
    dimensions.forEach(line=> {
      if (Number(line.height) > cargoDoorHeightLimit)
        heightLimitExceeded = true;
      if (!specialLoad && !spotBooking) {
        if (Number(line.length) > maxLength || Number(line.width) > maxWidth || Number(line.height) > maxHeight) {
          spotThresholdExceeded = true;
        }
      }
    });

    return {heightLimitExceeded, spotThresholdExceeded}
  }

  const renderUploadDiemsions = (canEdit) => {
    return (
      <DropzoneFileUpload
        objectType={"DIMENSIONS"}
        accept=".xls, .xlsx"
        dropzoneStyle={{ height: 38,  maxHeight: 38, minHeight: 38, border: "1px dashed #929292", maxWidth: "50%" }}
        inputLabel={{fontSize: 17, color: "#fd5b02"}}
        inputContent={"Drop an Excel file containing dimensions or Click to browse"}
        disabled={!canEdit}
        onChange={async (dimensions)=>{
          if (dimensions && Array.isArray(dimensions)) {
            const {maxLength, maxWidth, maxHeight, cargoDoorHeightLimit} = defaults;
            const {heightLimitExceeded, spotThresholdExceeded} = await checkRestrictions (dimensions);
            if (heightLimitExceeded) {
              SystemData.alert(
                "Height Limit Exceeded",
                `Pieces with height over ${cargoDoorHeightLimit}cm cannot be loaded onto aircraft due to cargo door limitations. One of the dimensions uploaded exceeds this limitation`
              );
              return;
            }

            if (spotThresholdExceeded) {
              SystemData.confirm(
                "Size Limit Exceeded for Regular Booking",
                parse(`Pieces with Length over <strong>${maxLength}cm</strong> or Width over <strong>${maxWidth}cm</strong> or Height over <strong>${maxHeight}cm</strong> require spot rate and review by Magma. Do you wish to transfer this booking to a Spot Request?`),
                [
                  {icon: "check_circle", value: true, label: "Yes", options: {color: "primary"}},
                  {icon: "cancel", value: false, label: "No", options: {color: "secondary"}}
                ],
                async (response) => {
                  if (response) {
                    // setSwitchToSpot(true);
                    dimensions.forEach(line=> {
                      gridApi.applyTransaction({add: [line]});
                    });

                    const dimensionsSummary = {switchToSpot: true};
                    const model = gridApi.getModel();
                    const visibleRows = model.rowsToDisplay;
                    calcSummaryData(dimensionsSummary, visibleRows);
                    if (props.onChange) {
                      props.onChange(dimensionsSummary);
                    }
                  } else {
                    return;
                  }
                }
              );
            } else {
              dimensions.forEach(line=> {
                gridApi.applyTransaction({add: [line]});
              });
            }
          }
        }}
      />
    )
  }

  const downloadTemplate = async () => {
    const {dimensionsUploadTemplate} = defaults;
    if (!dimensionsUploadTemplate || dimensionsUploadTemplate ==="")
      return;

    const url = `${apiSitePrefix}/bookings/downloadDimensionsTemplate/${dimensionsUploadTemplate}`;

    const response = await axios.get(url, {
      headers: {
        //'Access-Control-Allow-Headers': ['Origin', 'X-Requested-With', 'Content-Type', 'Accept'],
        'Access-Control-Allow-Origin': '*',
      }
    });

    if (response && response.data && response.data.success) {
      const fileName = response.data.downloadFileName;
      let a = document.createElement('a');
      a.href = response.data.excelSignedUrl;
      a.download = fileName;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); }, 0);
    }
  }

  // ==================
  // Grid functions
  // ==================
  const getEmptyRow = () => {
    const columns = getColumns({canEdit: state.canEdit});
    const emptyRow = {};
    if (columns && Array.isArray(columns)) {
      columns.forEach(column => {
        emptyRow[column.field] = "";
      })
    }
    return (emptyRow);
  };

  const sum = (values, col) => {
    let sum = 0;
    values.forEach( value => {
      sum += (isNaN(value.data[col]) ? 0 : Number(value.data[col]));
    });
    return sum;
  }

  const calcSummaryData = (dimensionsSummary, visibleRows) => {
    const columns = getColumns({canEdit: state.canEdit});

    if (columns && Array.isArray(columns)) {
      columns.forEach(column => {
        if (column.summary) {
          dimensionsSummary[column.field] = (column.summary ? sum(visibleRows, column.field) : null);
        }
      })
    }
    const shipmentParticulars = [];
    visibleRows.forEach(row => {
      shipmentParticulars.push(row.data);
    });
    dimensionsSummary["shipmentParticulars"] = shipmentParticulars;
  }

  const createSummaryData = (params) => {
    let model;
    let visibleRows = [];
    const activeApi = params.api ? params.api : gridApi;
    if(activeApi){
      model = activeApi.getModel();
      visibleRows = model.rowsToDisplay;
    }
    //console.log('model:', model);
    //console.log('visibleRows:', visibleRows);

    const dimensionsSummary = {};
    const currentColumn = params?.column;
    const {maxLength, maxWidth, maxHeight, cargoDoorHeightLimit} = defaults;

    if (currentColumn?.colId === "height" && Number(params.newValue) > cargoDoorHeightLimit) {
      SystemData.alert(
        "Height Limit Exceeded",
        `Pieces with height over ${cargoDoorHeightLimit}cm cannot be loaded onto aircraft due to cargo door limitations.`
      );
      params.node.data[currentColumn?.colId] = params.oldValue;
      calcSummaryData(dimensionsSummary, visibleRows);
      if (activeApi)
        activeApi.refreshCells();
      if (props.onChange) {
        props.onChange(dimensionsSummary);
      }
    } else {
      if (!specialLoad && !spotBooking) {
        if ( (currentColumn?.colId === "length" && Number(params.newValue) > maxLength ||
          currentColumn?.colId === "width" && Number(params.newValue) > maxWidth ||
          currentColumn?.colId === "height" && Number(params.newValue) > maxHeight)) {
          SystemData.confirm(
            "Size Limit Exceeded for Regular Booking",
            parse(`Pieces with Length over <strong>${maxLength}cm</strong> or Width over <strong>${maxWidth}cm</strong> or Height over <strong>${maxHeight}cm</strong> require spot rate and review by Magma. Do you wish to transfer this booking to a <strong>Spot Request</strong>?`),
            [
              {icon: "check_circle", value: true, label: "Yes", options: {color: "primary"}},
              {icon: "cancel", value: false, label: "No", options: {color: "secondary"}}
            ],
            async (response) => {
              if (response) {
                dimensionsSummary["switchToSpot"] = true;
                calcSummaryData(dimensionsSummary, visibleRows);
              } else {
                params.node.data[currentColumn?.colId] = params.oldValue;
                calcSummaryData(dimensionsSummary, visibleRows);
                if (activeApi)
                  activeApi.refreshCells();
              }
              if (props.onChange) {
                props.onChange(dimensionsSummary);
              }
            }
          );
        } else {
          if (currentColumn?.colId === "chargeableWeight" && Number(params.newValue) ) {
            params.node.data['volume'] = params.newValue * 0.006;
          }
          if (currentColumn?.colId === "volume" && Number(params.newValue) && Number(params.node.data.pieces) && Number(params.node.data.weightPerPiece)) {
            params.node.data['chargeableWeight'] = calculateChargeableWeight(params.node.data.pieces, params.node.data.weightPerPiece, params.newValue);
          }
          calcSummaryData(dimensionsSummary, visibleRows);
          if (props.onChange) {
            props.onChange(dimensionsSummary);
          }
        }
      } else {
        if (currentColumn?.colId === "chargeableWeight" && Number(params.newValue) ) {
          params.node.data['volume'] = params.newValue * 0.006;
        }
        calcSummaryData(dimensionsSummary, visibleRows);
        if (props.onChange) {
          props.onChange(dimensionsSummary);
        }
      }
    }

    // const columns = getColumns({canEdit: state.canEdit});
    //
    // if (columns && Array.isArray(columns)) {
    //   columns.forEach(column => {
    //     if (column.summary) {
    //       dimensionsSummary[column.field] = (column.summary ? sum(visibleRows, column.field) : null);
    //     }
    //   })
    // }
    // const shipmentParticulars = [];
    // visibleRows.forEach(row => {
    //   shipmentParticulars.push(row.data);
    // })
    // dimensionsSummary["shipmentParticulars"] = shipmentParticulars;

    // console.log('result:', dimensionsSummary);
    // if (props.onChange) {
    //   props.onChange(dimensionsSummary);
    // }
  }

  const handleRemoveItem = (data, api, node) => {
    if (!node)  return;
    if(api){
      const model = api.getModel();
      const visibleRows = model.rowsToDisplay;
      if (visibleRows.length <= 1)
        setState({...state, addButtonSuffix: ""});
    }
    api.applyTransaction({ remove: [data]});

    // SystemData.confirm(
    //   `Delete cargo particulars`,
    //   `Are you sure you wish to remove the ${itemToRemove}: '${params.contract[params.contract.length - 1]}'`,
    //   [
    //     {icon: "check_circle", value: true, label: "Yes", options: {color:"primary"}},
    //     {icon: "cancel", value: false, label: "No", options: {color:"secondary"}}
    //   ],
    //   (response) => {
    //     if (response) {
    //
    //     }
    //   }
    // );
  }

  const disableRemoveAll = () => {
    if (gridApi) {
      const model = gridApi.getModel();
      const visibleRows = model.rowsToDisplay;
      return (visibleRows.length < 1);
    }
  }

  const handleRemoveAll = () => {
    SystemData.confirm(
      `Delete cargo particulars`,
      `Are you sure you wish to remove all cargo particulars?'`,
      [
        {icon: "check_circle", value: true, label: "Yes", options: {color:"primary"}},
        {icon: "cancel", value: false, label: "No", options: {color:"secondary"}}
      ],
      (response) => {
        if (response && gridApi) {
          const model = gridApi.getModel();
          const visibleRows = model.rowsToDisplay;
          visibleRows.forEach(row => {
            gridApi.applyTransaction({ remove: [row.data]});
          });
          setState({...state, addButtonSuffix: ""});
        }
      }
    );
  }

  const onOverride = (params) => {
    if (params.newValue) {
      params.node.data.prevLength = params.node.data.length;
      params.node.data.prevWidth = params.node.data.width;
      params.node.data.prevHeight = params.node.data.height;
      params.node.data.length = "";
      params.node.data.width = "";
      params.node.data.height = "";
      params.node.data.overrideVolume = true;
    } else {
      params.node.data.length = params.node.data.prevLength ?? "";
      params.node.data.width = params.node.data.prevWidth ?? "";
      params.node.data.height = params.node.data.prevHeight ?? "";
      params.node.data.overrideVolume = false;
    }
    params.api.refreshCells();
  }

  const renderActionButtons = (params) => {
    return (
      <>
        {
          <IconButton
            color="secondary"
            onClick={() => { handleRemoveItem(params.data, params.api, params.node) }}
            disabled={params.disabled}
            size={"small"}
          >
            <DeleteOutline/>
          </IconButton>
        }
      </>
    )
  }

  const deckEditable = (params) => {
    const {lowerDeckMaxHeight} = defaults;
    if ( params.data.height && Number(params.data.height) > lowerDeckMaxHeight) {
      return false;
    } else {
      return true;
    }
  }

  // ==================
  // Value Getters
  // ==================
  const hashValueGetter = function (params) {
    if (params.node.rowIndex === 0 && state.addButtonSuffix !== " with different dimensions"){
       setState({...state, addButtonSuffix: " with different dimensions"});
    }
    return params.node.rowIndex + 1;
  };

  const volumeValueGetter = function (params) {
    if (params.data.overrideVolume) {
      return params.data.volume;
    }
    if (params.data.length && params.data.width && params.data.height && params.data.pieces) {
      const volume = Number(params.data.length) * Number(params.data.width) * Number(params.data.height) * Number(params.data.pieces) / 1000000;
      return volume;
    }
    return "N/A"
  };

  const actualWeightGetter = function (params) {
    if (params.data.pieces && params.data.weightPerPiece) {
      return Number(params.data.pieces) * Number(params.data.weightPerPiece);
    }
    return "N/A"
  };

  const calculateChargeableWeight = function (pieces, weightPerPiece, volume) {
    if (pieces && weightPerPiece && volume !== "N/A") {
      const totalWeight = Number(pieces) * Number(weightPerPiece);
      const chargeableWeight = volume / 0.006;
      return (chargeableWeight > totalWeight ? chargeableWeight : totalWeight);
    }
  }
  const chargeableWeightGetter = function (params) {
    if (internalUser && params.data.overrideVolume && Number(params.data.chargeableWeight)) {
      return params.data.chargeableWeight;
    }
    if (params.data.pieces && params.data.weightPerPiece && params.data.volume !== "N/A") {
      const volume = params.data.overrideVolume ? params.data.volume : Number(params.data.length) * Number(params.data.width) * Number(params.data.height) * Number(params.data.pieces) / 1000000;
      return calculateChargeableWeight(params.data.pieces, params.data.weightPerPiece, volume);
    }
    return "N/A"
  };

  const deckValueGetter = function (params) {
    const {lowerDeckMaxHeight} = defaults;
    if ((params.data.height && Number(params.data.height) > lowerDeckMaxHeight) &&
         params.data.deckRequired !== "MAIN" ) {
      return "MAIN";
    }
    if ( params.data.deckRequired !== "MAIN" && params.data.deckRequired !== "LOWER" ) {
      return "LOWER";
    }
    return params.data.deckRequired;
  };

  // ==================
  // The grid structure
  // ==================
  const numericColumnEditor = {
    component: "MaskedNumber",
    params: {
      isdatagrid: true,
      value: props.value,
      decimalScale: 2,
      fixedDecimalScale: true,
      // onChange: props.onChange,
      style: {width:"100%", fontSize: "0.875rem", textAlign: "right"}
    }
  }

  const lookupValues = {"MAIN": "Main Deck", "LOWER": "Lower Deck"};

  const getColumns = (props) => {
    const baseColumn = {
      options: {
        cellStyle: { fontSize: 7, },
        cellClass: classes.column,
        headerClass: classes.header,
      },
      editable: state.canEdit,
      headerClass: 'classes',
      sortable: false
    };

    return [
      {
        headerName: "#",
        ...baseColumn,
        field: "sequence",
        valueGetter: (params) => {
          const sequence = hashValueGetter(params);
          params.node.data.sequence = sequence;
          return sequence;
        },
        maxWidth: 30,
        summary: false,
        editable: false,
      },
      {
        headerName: "Pieces",
        ...baseColumn,
        field: "pieces",
        width: 60,
        type: "rightAligned",
        summary: true,
        onCellValueChanged: createSummaryData,
      },
      {
        headerName: "Override CBM",
        ...baseColumn,
        field: "overrideVolume",
        width: 110,
        summary: false,
        editable: false,
        cellRenderer: {
          component: "CheckboxEditor",
          params: {
            id: "overrideVolume",
            isdatagrid: true,
            disabled: !state.canEdit,
          }
        },
        onCellValueChanged: onOverride,
      },
      {
        headerName: "Length (cm)",
        ...baseColumn,
        options: {
          cellClass: classes.numericColumn,
          headerClass: classes.numericHeader,
        },
        field: "length",
        type: "rightAligned",
        width: 95,
        editable: (params) => (!params.data.overrideVolume),
        summary: false,
        onCellValueChanged: createSummaryData,
      },
      {
        headerName: "Width (cm)",
        ...baseColumn,
        options: {
          cellClass: classes.numericColumn,
          headerClass: classes.numericHeader,
        },
        field: "width",
        type: "rightAligned",
        width: 95,
        editable: (params) => (!params.data.overrideVolume),
        summary: false,
        onCellValueChanged: createSummaryData,
      },
      {
        headerName: "Height (cm)",
        ...baseColumn,
        options: {
          cellClass: classes.numericColumn,
          headerClass: classes.numericHeader,
        },
        field: "height",
        type: "rightAligned",
        width: 95,
        editable: (params) => (!params.data.overrideVolume),
        summary: false,
        onCellValueChanged: createSummaryData,
      },
      {
        headerName: "Volume (CBM)",
        ...baseColumn,
        options: {
          cellClass: classes.numericColumn,
          headerClass: classes.numericHeader,
        },
        field: "volume",
        type: "rightAligned",
        width: 110,
        editable: (params) => (params.data.overrideVolume),
        summary: true,
        valueGetter: (params) => {
          const volume = volumeValueGetter(params);
          params.node.data.volume = volume;
          return volume;
        },
        valueFormatter: (params)=> {
          const volume = volumeValueGetter(params);
          return (isNaN(volume) ? "N/A" : (Math.round(volume * 100) / 100).toFixed(2))
        },
        onCellValueChanged: createSummaryData,
      },
      {
        headerName: "Piece Wt (Kg)",
        ...baseColumn,
        options: {
          cellClass: classes.numericColumn,
          headerClass: classes.numericHeader,
        },
        field: "weightPerPiece",
        type: "rightAligned",
        width: 110,
        summary: false,
        onCellValueChanged: createSummaryData,
      },
      {
        headerName: "Total Wt (Kg)",
        ...baseColumn,
        options: {
          cellClass: classes.numericColumn,
          headerClass: classes.numericHeader,
        },
        field: "actualWeight",
        type: "rightAligned",
        width: 100,
        editable: false,
        summary: true,
        valueGetter: (params) => {
          const actualWeight = actualWeightGetter(params);
          params.node.data.actualWeight = actualWeight;
          return actualWeight;
        },
        valueFormatter: (params)=> {
          const actualWeight = actualWeightGetter(params);
          return (isNaN(actualWeight) ? "N/A" : (Math.round(actualWeight * 100) / 100).toFixed(2))
        }
      },
      {
        headerName: "Chargeable Wt (Kg)",
        ...baseColumn,
        options: {
          cellClass: classes.numericColumn,
          headerClass: classes.numericHeader,
        },
        field: "chargeableWeight",
        type: "rightAligned",
        width: 140,
        editable: (params) => (params.data.overrideVolume && internalUser),
        onCellValueChanged: createSummaryData,
        summary: true,
        valueGetter: (params) => {
          const chargeableWeight = chargeableWeightGetter (params);
          params.node.data.chargeableWeight = chargeableWeight;
          return chargeableWeight;
        },
        valueFormatter: (params)=> {
          const chargeableWeight = chargeableWeightGetter(params);
          return (isNaN(chargeableWeight) ? "N/A" : (Math.round(chargeableWeight * 100) / 100).toFixed(2))
        }
      },
      {
        headerName: "Cargo Profile",
        ...baseColumn,
        field: "deckRequired",
        cellEditor: {
          component: "Selector",
          params: {
            isdatagrid: true,
            value: props.value,
            lookupValues: lookupValues,
            style: {width:"100%", fontSize: "0.875rem", textAlign: "right"},
            //onChange: (value) => {props.onChange(value)}
            // onChange: (value) => {
            //   // const rowNode = gridApi.getRow('aa');
            //   // var newPrice = Math.floor(Math.random() * 100000);
            //   // rowNode.setDataValue('price', newPrice);
            //   props.onChange(value);
            // }
          }
        },
        editable: (params) => (deckEditable(params)),
        summary: false,
        flex: 1,
        valueFormatter: (params) => {
          if (params.value === "") {
            params.node.data.deckRequired = "LOWER";
            return  "Lower Deck";
          } else {
            return lookupValues[params.node.data.deckRequired];
          }
        },
        valueGetter: (params) => {
          const deckRequired = deckValueGetter(params);
          params.node.data.deckRequired = deckRequired;
          return deckRequired;
        },
        onCellValueChanged: createSummaryData,
      },
      {
        ...baseColumn,
        field: 'deleteAction',
        headerName: ' ',
        editable: false,
        cellRenderer: {
          component: "renderActionButtons",
          params: {
            disabled: !state.canEdit,
          }
        },
        maxWidth: 50,
        cellStyle: { border: "none" },
      },
    ];
  };

  return (
    // <div style={{width: props.width?props.width:"100%", ...props.style}} className="ag-theme-material">
    <div style={{width: props.width?props.width:"100%", ...props.style}} >

      <div style={{display: "flex"}}>
        <Button
          style={{height:24, marginTop:4}}
          variant="text"
          color="primary"
          disabled={!props.canEdit}
          startIcon={<AddBoxOutlined/>}
          //className={classes.button}
          onClick={()=>{
            setAutoHeight();
            gridApi.applyTransaction({add: [getEmptyRow()]})
          }}
        >
          {`Add Cargo ${state.addButtonSuffix}`}
        </Button>
        <Button
          style={{height:24, marginTop:4}}
          variant="text"
          color="primary"
          disabled={!props.canEdit || disableRemoveAll()}
          startIcon={<DeleteOutline/>}
          //className={classes.button}
          onClick={()=>{
            setAutoHeight();
            handleRemoveAll();
          }}
        >
          Remove All
        </Button>
        <Button
          style={{height:24, marginTop:4}}
          variant="text"
          color="primary"
          disabled={!props.canEdit || !defaults.dimensionsUploadTemplate}
          startIcon={<FileDownload/>}
          //className={classes.button}
          onClick={()=>{
            downloadTemplate();
          }}
        >
          Download Excel Template
        </Button>
        {renderUploadDiemsions(props.canEdit)}
      </div>

      <DataGrid
        title="Dimensions"
        ref={dataGridRef}
        components={{renderActionButtons}}
        columns={getColumns({canEdit: state.canEdit})}
        rows={dimensions}
        height={props.height||`calc(100vh - ${(290)}px)`}
        onGridReady={onGridReady}
        onRowSelected={onRowSelected}
        onRowDataUpdated={onRowDataUpdated}
        //groupIncludeFooter={true}
        //groupIncludeTotalFooter={true}

        options={{
          singleClickEdit: true,
          //editType:'fullRow',
          //onCellValueChanged: (params)=>{createSummaryData(params)}, //prevent default onCellonCellValueChanged
          defaultColDef: {
            editable: true,
            resizable: false,
            menuTabs: []
          },
          overlayLoadingTemplate:
            '<span class="ag-overlay-loading-center">Please wait while your data are loading</span>',
          overlayNoRowsTemplate:
            '<span style="padding: 10px; border: 1px solid #111; background: #fdecea;"> Click   \'Add Cargo\' to provide shipment particulars</span>',
        }}
      />
    </div>
  );

}

export default memo(withStyles(styles)(BookingDimensionsGrid));
